// Scrollable multi-month calendar for the Trip Builder list: months stacked
// vertically from the current month onward, each a 7-column week grid with one
// continuous bar per client stay (arrival → departure). Tapping a bar opens the
// client's itinerary in the builder.
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const pad = (n) => String(n).padStart(2, "0");
const iso = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;

const MonthBlock = ({ y, m, stays, todayKey, onOpen }) => {
  const firstWeekday = (new Date(y, m, 1).getDay() + 6) % 7; // Monday-first
  const start = new Date(y, m, 1 - firstWeekday);
  const weeks = [];
  const d = new Date(start);
  while (weeks.length < 6) {
    const wk = Array.from({ length: 7 }, () => {
      const dd = new Date(d);
      d.setDate(d.getDate() + 1);
      return dd;
    });
    weeks.push(wk);
    if (d.getMonth() !== m || weeks.length === 6) break;
  }
  const label = new Date(y, m, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <div data-testid={`trip-calendar-month-${y}-${pad(m + 1)}`} className="border border-neutral-800/80 bg-[#0c0c10]">
      <p className="border-b border-neutral-800/70 px-3 py-2 font-serif text-lg font-light text-neutral-50">{label}</p>
      <div className="grid grid-cols-7 border-b border-neutral-800/70">
        {WEEKDAYS.map((w) => (
          <div key={w} className="px-1.5 py-1.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-amber-300/60">{w}</div>
        ))}
      </div>
      {weeks.map((wk, wi) => {
        const wkStart = iso(wk[0]);
        const wkEnd = iso(wk[6]);
        const overlapping = stays
          .filter((t) => t.arrival_date <= wkEnd && t.departure_date >= wkStart)
          .sort((a, b) => a.arrival_date.localeCompare(b.arrival_date) || b.departure_date.localeCompare(a.departure_date));
        const lanes = [];
        const bars = overlapping.map((t) => {
          const cs = t.arrival_date > wkStart ? wk.findIndex((x) => iso(x) === t.arrival_date) + 1 : 1;
          const ce = t.departure_date < wkEnd ? wk.findIndex((x) => iso(x) === t.departure_date) + 1 : 7;
          let lane = 0;
          while ((lanes[lane] || []).some((b) => !(b.ce < cs || b.cs > ce))) lane++;
          (lanes[lane] = lanes[lane] || []).push({ cs, ce });
          return { t, cs, ce, lane };
        });
        return (
          <div
            key={wi}
            className="grid grid-cols-7 border-b border-neutral-800/60 last:border-b-0"
            style={{ gridTemplateRows: `24px repeat(${Math.max(1, lanes.length)}, minmax(19px, auto))` }}
          >
            {wk.map((day, di) => (
              <div
                key={di}
                className={`border-r border-neutral-800/40 px-1.5 py-1 text-right text-[10px] last:border-r-0 ${
                  day.getMonth() === m ? (iso(day) === todayKey ? "font-bold text-amber-300" : "text-neutral-300") : "text-neutral-700"
                }`}
              >
                {day.getDate()}
              </div>
            ))}
            {bars.map(({ t, cs, ce, lane }) => (
              <button
                key={`${t.id}-${wi}`}
                type="button"
                data-testid={`trip-cal-bar-${String(t.id).slice(0, 6)}-w${wi}`}
                onClick={() => onOpen(t)}
                title={`${t.client?.name} — arrives ${t.arrival_date}, leaves ${t.departure_date} · ${t.nights} night${t.nights === 1 ? "" : "s"}${t.pax ? ` · ${t.pax} guests` : ""} · tap to open`}
                style={{ gridColumn: `${cs} / ${ce + 1}`, gridRow: lane + 2 }}
                className={`mx-0.5 my-0.5 cursor-pointer truncate border-l-2 px-1.5 py-0.5 text-left text-[9px] font-semibold uppercase tracking-wide transition-colors ${
                  t.status === "draft"
                    ? "border-dashed border-neutral-500 bg-neutral-500/10 text-neutral-400 hover:bg-neutral-500/25"
                    : "border-amber-400 bg-amber-500/15 text-amber-200 hover:bg-amber-500/30"
                }`}
              >
                {t.client?.name} · {t.nights} n
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export const TripCalendar = ({ trips, onOpen }) => {
  const stays = (trips || [])
    .filter((t) => t.arrival_date && t.departure_date)
    .map((t) => ({
      ...t,
      nights: Math.max(1, Math.round((new Date(`${t.departure_date}T00:00:00`) - new Date(`${t.arrival_date}T00:00:00`)) / 86400000)),
    }));
  const now = new Date();
  const startIdx = now.getFullYear() * 12 + now.getMonth();
  // at least 3 months ahead; extend to cover the furthest departure (+1 month); cap at 14
  let endIdx = startIdx + 2;
  stays.forEach((t) => {
    const d = new Date(`${t.departure_date}T00:00:00`);
    endIdx = Math.max(endIdx, d.getFullYear() * 12 + d.getMonth() + 1);
  });
  endIdx = Math.min(endIdx, startIdx + 14);
  const months = [];
  for (let i = startIdx; i <= endIdx; i++) months.push({ y: Math.floor(i / 12), m: i % 12 });

  return (
    <section data-testid="trip-calendar" className="scroll-mt-6">
      <div className="mb-4 border-b border-neutral-800/70 pb-3">
        <p data-testid="trip-calendar-head" className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Calendar</p>
        <p className="mt-1 text-xs text-neutral-500">
          {stays.length} client{stays.length === 1 ? "" : "s"} with dates · about 6 weeks visible — scroll inside the grid for later months · tap a bar to open that itinerary · faded = draft not yet sent
        </p>
      </div>
      <div
        data-testid="trip-calendar-scroll"
        className="max-h-[23rem] space-y-4 overflow-y-auto scroll-smooth border border-neutral-800/60 bg-black/20 p-2"
      >
        {months.map(({ y, m }) => (
          <MonthBlock key={`${y}-${m}`} y={y} m={m} stays={stays} todayKey={iso(now)} onOpen={onOpen} />
        ))}
      </div>
      <p className="mt-2 text-right text-[9px] uppercase tracking-[0.2em] text-neutral-600">Scroll inside the calendar for later months →</p>
    </section>
  );
};
